import {
  AyaLane,
  AyaRunStatusDto,
} from '../models/aya-conversation.model';
import { isRunLifecycleStatus } from './aya-progress.util';

export interface AyaSearchEvent {
  state: string;
  query?: string;
  label?: string;
}

export interface AyaPhaseEvent {
  phase: string;
  label?: string;
}

export interface AyaStreamHandlers {
  onDelta: (text: string) => void;
  onMessage?: (content: string) => void;
  onSearch?: (event: AyaSearchEvent) => void;
  onPhase?: (event: AyaPhaseEvent) => void;
  onStatus?: (text: string) => void;
  onToolCall?: (event: { name: string }) => void;
  onDone?: (info: { assistantMessageId?: string | null }) => void;
  onError?: (error: Error) => void;
}

export interface AyaStreamContext {
  apiBase: string;
  lane: AyaLane;
  getAuthHeaders: () => Promise<Record<string, string>>;
  getRunStatus: (runId: string) => Promise<AyaRunStatusDto>;
}

interface SseFrame {
  event: string;
  data: string;
  id: string | null;
}

const MAX_RECONNECTS = 5;

/** Map upstream stream URL onto the Ask Aya BFF base. */
export function ayaProxyUrl(apiBase: string, pathOrUrl: string): string {
  const base = apiBase.replace(/\/+$/, '');
  if (/^https?:\/\//i.test(pathOrUrl)) {
    const u = new URL(pathOrUrl);
    return `${base}${u.pathname}${u.search}`;
  }
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return path.startsWith(`${base}/`) ? path : `${base}${path}`;
}

function withAfterSeq(apiBase: string, url: string, afterSeq: number): string {
  const resolved = ayaProxyUrl(apiBase, url);
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const u = new URL(resolved, origin);
  u.searchParams.set('after_seq', String(afterSeq));
  return u.toString();
}

function extractDelta(payload: unknown): string {
  if (typeof payload === 'string') return payload;
  if (!payload || typeof payload !== 'object') return '';
  const p = payload as Record<string, unknown>;
  for (const key of ['delta', 'token', 'text', 'content', 'value']) {
    const v = p[key];
    if (typeof v === 'string') return v;
  }
  return '';
}

function extractPhase(payload: unknown): AyaPhaseEvent | null {
  if (!payload || typeof payload !== 'object') {
    if (typeof payload === 'string' && payload.trim()) {
      return { phase: payload.trim().toLowerCase(), label: payload.trim() };
    }
    return null;
  }
  const p = payload as Record<string, unknown>;
  const raw =
    (typeof p.id === 'string' && p.id) ||
    (typeof p.phase === 'string' && p.phase) ||
    (typeof p.name === 'string' && p.name) ||
    (typeof p.step === 'string' && p.step) ||
    '';
  const label =
    (typeof p.label === 'string' && p.label) ||
    (typeof p.message === 'string' && p.message) ||
    undefined;
  if (!raw && !label) return null;
  return { phase: (raw || label || '').toLowerCase(), label };
}

function extractStatusText(payload: unknown): string {
  if (typeof payload === 'string') {
    const t = payload.trim();
    return t && !isRunLifecycleStatus(t) ? t : '';
  }
  if (!payload || typeof payload !== 'object') return '';
  const p = payload as Record<string, unknown>;
  for (const key of ['message', 'label', 'text', 'value']) {
    const v = p[key];
    if (typeof v === 'string') {
      const t = v.trim();
      if (t && !isRunLifecycleStatus(t)) return t;
    }
  }
  const status =
    typeof p.status === 'string' ? p.status.trim() : '';
  if (status && !isRunLifecycleStatus(status)) return status;
  return '';
}

function parseData(data: string): unknown {
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}

function* parseFrames(chunk: string): Generator<SseFrame> {
  for (const raw of chunk.split(/\r?\n\r?\n/)) {
    const block = raw.trim();
    if (!block) continue;
    let event = 'message';
    let id: string | null = null;
    const dataLines: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith(':')) continue;
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
      else if (line.startsWith('id:')) id = line.slice(3).trim();
    }
    yield { event, data: dataLines.join('\n'), id };
  }
}

function laneHeaderRecord(lane: AyaLane): Record<string, string> {
  return {
    'X-Profile': lane.profile,
    'X-Aya-User-Id': lane.userId,
    'X-Aya-Org-Id': lane.orgId,
  };
}

/** Reads an Aya run SSE stream with reconnect via after_seq. */
export async function streamAyaRun(
  ctx: AyaStreamContext,
  streamUrl: string,
  runId: string,
  handlers: AyaStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  let afterSeq = 0;
  let finished = false;
  let assistantMessageId: string | null | undefined;
  let attempts = 0;

  while (!finished) {
    if (signal?.aborted) return;
    try {
      const authHeaders = await ctx.getAuthHeaders();
      const headers: Record<string, string> = {
        ...laneHeaderRecord(ctx.lane),
        ...authHeaders,
        Accept: 'text/event-stream',
      };
      const res = await fetch(withAfterSeq(ctx.apiBase, streamUrl, afterSeq), {
        method: 'GET',
        headers,
        signal,
      });
      if (!res.ok || !res.body) {
        throw new Error(`Stream failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        let text = buffer + decoder.decode(value, { stream: true });
        let carry = '';
        if (text.endsWith('\r')) {
          carry = '\r';
          text = text.slice(0, -1);
        }
        buffer = text.replace(/\r\n/g, '\n') + carry;

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const complete = buffer.slice(0, boundary + 2);
          buffer = buffer.slice(boundary + 2);

          for (const frame of parseFrames(complete)) {
            if (frame.id) {
              const seq = Number.parseInt(frame.id, 10);
              if (Number.isFinite(seq)) afterSeq = Math.max(afterSeq, seq);
            }
            const payload = parseData(frame.data);
            const record =
              payload && typeof payload === 'object'
                ? (payload as Record<string, unknown>)
                : null;

            switch (frame.event) {
              case 'token':
              case 'delta':
              case 'text': {
                const delta = extractDelta(payload);
                if (delta) handlers.onDelta(delta);
                break;
              }
              case 'search': {
                const state = typeof record?.state === 'string' ? record.state : '';
                const query = typeof record?.query === 'string' ? record.query : undefined;
                const label = typeof record?.label === 'string' ? record.label : undefined;
                if (state || label || query) handlers.onSearch?.({ state, query, label });
                break;
              }
              case 'phase': {
                const phase = extractPhase(payload);
                if (phase) handlers.onPhase?.(phase);
                break;
              }
              case 'status': {
                const statusText = extractStatusText(payload);
                if (statusText) handlers.onStatus?.(statusText);
                break;
              }
              case 'tool_call': {
                const name =
                  (typeof record?.name === 'string' && record.name) ||
                  (typeof record?.tool === 'string' && record.tool) ||
                  '';
                if (name) handlers.onToolCall?.({ name });
                break;
              }
              case 'message': {
                const content = record
                  ? typeof record.content === 'string'
                    ? record.content
                    : extractDelta(payload)
                  : extractDelta(payload);
                if (typeof record?.id === 'string') assistantMessageId = record.id;
                if (typeof record?.message_id === 'string') {
                  assistantMessageId = record.message_id;
                }
                if (content) handlers.onMessage?.(content);
                break;
              }
              case 'done':
              case 'end':
              case 'complete': {
                if (typeof record?.assistant_message_id === 'string') {
                  assistantMessageId = record.assistant_message_id;
                }
                finished = true;
                break;
              }
              case 'error': {
                const message =
                  (typeof record?.error === 'string' && record.error) ||
                  (typeof record?.message === 'string' && record.message) ||
                  'Aya could not finish this response.';
                finished = true;
                handlers.onError?.(new Error(message));
                return;
              }
              default: {
                const delta = extractDelta(payload);
                if (delta) handlers.onDelta(delta);
                break;
              }
            }
            if (finished) break;
          }
          if (finished) break;
          boundary = buffer.indexOf('\n\n');
        }
        if (finished) break;
      }

      if (!finished) {
        const status = await ctx.getRunStatus(runId).catch(() => null);
        if (status && status.status !== 'running' && status.status !== 'queued') {
          finished = true;
          if (status.error) {
            handlers.onError?.(new Error(status.error));
            return;
          }
          assistantMessageId = status.assistant_message_id ?? assistantMessageId;
        } else {
          attempts += 1;
          if (attempts > MAX_RECONNECTS) {
            handlers.onError?.(new Error('Lost connection to Aya.'));
            return;
          }
          await new Promise((r) => setTimeout(r, Math.min(4000, 400 * attempts)));
        }
      }
    } catch (err) {
      if (signal?.aborted) return;
      attempts += 1;
      if (attempts > MAX_RECONNECTS) {
        handlers.onError?.(
          err instanceof Error ? err : new Error('Lost connection to Aya.')
        );
        return;
      }
      await new Promise((r) => setTimeout(r, Math.min(4000, 400 * attempts)));
    }
  }

  handlers.onDone?.({ assistantMessageId });
}
